import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Modal } from "app/components/Modal";
import { DropdownIcon } from "app/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { emailRegex } from "styles/theme/utils";
import {
  valueForOnlyNumericInputTextFields,
  valueForOnlyStringInputTextFields,
} from "utils/helpers";

import {
  ADD_CUSTOMER_FIELDS,
  REQUIRED_CONTACT_FIELDS,
} from "../../../../data";
import {
  selectContcatForm,
  selectContactSaving,
  selectIsAddContactModalOpen,
} from "../../../../redux/selector";
import { customersActions } from "../../../../redux/slice";
import {
  contactForm,
  fieldConfig,
  CustomerSelectOption,
} from "../../../../types";
import { useStyles } from "./styles";

const modal = "CUSTOMERS.MODAL";

const AddContactModal = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddContactModalOpen);
  const form = useSelector(selectContcatForm);
  const saving = useSelector(selectContactSaving);


  const onClose = () => {
    if (saving) return;
    dispatch(customersActions.closeAddContactModal());
  };

  const setField = (name: keyof contactForm, value: string) => {
    const nextValue =
      name === "phoneNo"
        ? valueForOnlyNumericInputTextFields(value)
        : name === "contactName" || name === "role"
          ? valueForOnlyStringInputTextFields(value)
          : value;

    dispatch(customersActions.setContactFormField({ name, value: nextValue }));
  };


  const placeholder = (field: fieldConfig) =>
    t(`${modal}.PLACEHOLDERS.${field.placeholder}`);

  const optionLabel = (option: CustomerSelectOption) =>
    option.labelKey ? t(option.labelKey) : option.label || option.value;

  const email = form.email.trim();
  const emailError =
    email && !emailRegex.test(email)
      ? "CUSTOMERS.MODAL.VALIDATION.EMAIL_INVALID"
      : "";
  const isValid =
    REQUIRED_CONTACT_FIELDS.every((key) => Boolean(form[key]?.trim())) &&
    !emailError;

  const onAdd = () => {
    if (!isValid || saving) return;
    dispatch(customersActions.addCustomerContact());
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>{t(`${modal}.ADD_TITLE`)}</Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("BUTTONS.CANCEL")}
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          {ADD_CUSTOMER_FIELDS.map((field) => {
            const { key, label, type, options = [] } = field;
            const contactKeys = (key) as keyof contactForm 

            return (
              <Box key={contactKeys} className={styles.field}>
                <Typography className={styles.label}>
                  {t(`${modal}.FIELDS.${label}`)}
                </Typography>
                <TextField
                  className={styles.input}
                  fullWidth
                  select={type === "select"}
                  variant="outlined"
                  placeholder={placeholder(field)}
                  value={form[key]}
                  inputMode={
                    key === "phoneNo"
                      ? "numeric"
                      : key === "email"
                        ? "email"
                        : "text"
                  }
                  disabled={saving}
                  InputProps={{ notched: false }}
                  SelectProps={
                    type === "select"
                      ? {
                          displayEmpty: true,
                          IconComponent: DropdownIcon,
                          renderValue: (value) => {
                            const selected = options.find(
                              (option) => option.value === value
                            );
                            return selected
                              ? optionLabel(selected)
                              : placeholder(field);
                          },
                        }
                     : {}
                  }
                  onChange={(event) => setField(contactKeys, event.target.value)}
                >
                  {type === "select"
                    ? [
                        <MenuItem key="empty" value="" disabled>
                          {placeholder(field)}
                        </MenuItem>,
                        ...options.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {optionLabel(option)}
                          </MenuItem>
                        )),
                      ]
                    : []}
                </TextField>
              </Box>
            );
          })}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={onClose}
            disabled={saving}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!isValid || saving}
            onClick={() => onAdd()
            }
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddContactModal;
